import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Link from 'next/link';
import React from 'react';
import ProfileAvatar from './ProfileAvatar';
import WalletAddress from './WalletAddress';

dayjs.extend(relativeTime);

const fetchCompletedJobs = async () => {
  const response = await fetch('/api/jobs/completed?limit=20');
  if (!response.ok) {
    throw new Error('Failed to fetch completed jobs');
  }
  const data = await response.json();
  return data.results;
};

const CompletedJobsFeed = () => {
  const {
    data: jobs = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['completedJobs'],
    queryFn: fetchCompletedJobs,
    refetchOnWindowFocus: false,
    refetchInterval: 30000,
    staleTime: 20000,
  });

  if (isLoading) {
    return (
      <div className='max-w-4xl mx-auto w-full mt-20'>
        <h2 className='text-center mb-4 text-white font-bold text-2xl'>Recent Job Completions</h2>
        <div className='rounded-lg bg-gray-900 p-8 max-h-[400px] overflow-y-scroll'>
          <div className='space-y-4'>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className='flex space-x-4 p-3'>
                <div className='rounded-full bg-gray-700 h-10 w-10'></div>
                <div className='flex-1 space-y-2 py-1'>
                  <div className='h-4 bg-gray-700 rounded w-3/4'></div>
                  <div className='h-3 bg-gray-700 rounded w-1/2'></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='max-w-4xl mx-auto w-full mt-20'>
        <h2 className='text-center mb-4 text-white font-bold text-2xl'>Recent Job Completions</h2>
        <div className='rounded-lg bg-gray-900 p-8 max-h-[400px] overflow-y-scroll'>
          <div className='text-center py-8 text-gray-400'>
            <p>Failed to load job completions. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-4xl mx-auto w-full mt-20'>
      <h2 className='text-center mb-4 text-white font-bold text-2xl'>Recent Job Completions</h2>
      <div className='rounded-lg bg-gray-900 p-2 md:p-8 max-h-[400px] overflow-y-scroll'>
        {jobs.length === 0 ? (
          <div className='text-center py-8 text-gray-400'>
            <p>No jobs completed yet.</p>
          </div>
        ) : (
          <ul className='space-y-1'>
            {jobs.map((job) => (
              <CompletedJobsFeedItem key={job.id} job={job} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const CompletedJobsFeedItem = ({ job }) => {
  const completedAt = job.completedAt?.toDate ? job.completedAt.toDate() : new Date(job.completedAt);
  const finalReward = job.finalReward || job.reward;

  return (
    <li className='flex md:flex-row gap-4 flex-col justify-between odd:bg-gray-800 p-3 rounded-lg text-sm hover:bg-gray-700 transition-colors'>
      <div className='flex items-center gap-4 flex-1'>
        <div className='w-12 h-12 flex-shrink-0 flex items-center justify-center'>
          <ProfileAvatar width={48} address={job.address} />
        </div>

        <div className='flex-1 min-w-0'>
          <p className='text-gray-200'>
            🎉{' '}
            <Link href={`/profile/${job.address}`}>
              <a className='text-white font-medium hover:text-primary-500 transition-colors'>
                <WalletAddress address={job.address} />
              </a>
            </Link>{' '}
            completed <span className='text-primary-500 font-medium'>{job.title}</span>
          </p>

          {job.completionMessage && (
            <div className='mt-1 max-w-[420px]'>
              <p className='text-xs text-gray-400 italic'>"{job.completionMessage}"</p>
            </div>
          )}

          <div className='mt-1 flex flex-wrap gap-2 text-xs text-gray-400'>
            <span>💰 {finalReward} treats</span>
            {job.rating && <span className='flex items-center gap-1'>⭐ {job.rating}/5</span>}
          </div>
        </div>
      </div>

      <div className='flex items-center gap-4 text-gray-400 flex-shrink-0'>
        <span className='text-xs'>{dayjs(completedAt).fromNow()}</span>
      </div>
    </li>
  );
};

export default CompletedJobsFeed;
